import React, { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";

import { useAuth } from "@/hooks/useAuth";
import { useDemoStatus } from "@/hooks/useDemoStatus";
import apiService from "@/services/api";
import { useAppStore } from "@/store/appStore";
import { NO_BALANCE_EVENT, type NoBalanceDetail } from "@/utils/billingEvents";
import { cn } from "@/utils";

const BILLING_PAGE = "organization_billing";

export const NoBalanceBanner: React.FC = () => {
  const { user } = useAuth();
  const currentPage = useAppStore((s) => s.currentPage);
  const setCurrentPage = useAppStore((s) => s.setCurrentPage);

  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [detail, setDetail] = useState<NoBalanceDetail | null>(null);
  // EXTERNAL-организацией тариф управляет администрация — клиенту покупка недоступна,
  // поэтому кнопку «Купить пакет» в баннере не показываем (бэкенд её и так отбивает 403).
  const [isExternalManaged, setIsExternalManaged] = useState(false);

  const isClient = user?.role === "CLIENT";
  // В демо денег нет вовсе — проверки считает квота, её показывает DemoBanner.
  const demo = useDemoStatus();

  // Проверяем доступный объём при mount у CLIENT — если у сотрудника нет
  // средств (с учётом его персонального лимита), показываем баннер сразу.
  useEffect(() => {
    if (!isClient) return;
    let cancelled = false;
    Promise.all([
      apiService.getBillingBalance().catch(() => null),
      apiService.getMySpendingLimit().catch(() => null),
    ]).then(([balanceRes, limitRes]) => {
      if (cancelled) return;
      setIsExternalManaged(balanceRes?.management_type === "EXTERNAL");
      const orgBalance = balanceRes?.total_available ?? 0;
      const myAvailable =
        limitRes?.has_limits && limitRes.cap_remaining != null
          ? Math.min(limitRes.cap_remaining, orgBalance)
          : orgBalance;
      if (myAvailable <= 0) {
        setVisible(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isClient]);

  // Ловим события «нет средств» из интерсептора API.
  useEffect(() => {
    if (!isClient) return;
    const handler = (event: Event) => {
      const ce = event as CustomEvent<NoBalanceDetail>;
      setDetail(ce.detail || null);
      setVisible(true);
      setDismissed(false);
    };
    window.addEventListener(NO_BALANCE_EVENT, handler);
    return () => window.removeEventListener(NO_BALANCE_EVENT, handler);
  }, [isClient]);

  if (!isClient) return null;
  if (demo?.is_demo) return null;
  if (!visible || dismissed) return null;
  if (currentPage === BILLING_PAGE) return null;

  const title =
    detail?.code === "BALANCE_EXPIRED"
      ? "Срок действия пакета истёк"
      : detail?.code === "USER_LIMIT_EXCEEDED"
        ? "Лимит расходов исчерпан"
        : "Нет активного сервисного пакета";

  const description =
    detail?.message ||
    (isExternalManaged
      ? "Тариф вашей организации назначен администрацией. Для продления обратитесь к администратору платформы."
      : "Для выполнения платных проверок (МВД, ФНС, ФССП, суды и др.) нужен активный сервисный пакет. Бесплатные модули валидации (ИНН, СНИЛС, телефон, e-mail) продолжают работать.");

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-lg border border-line bg-surface-elevated px-4 py-2.5"
      role="alert"
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
      <div className="min-w-0 flex-1 text-fs-sm leading-relaxed">
        <span className="font-medium text-ink-1">{title}.</span> <span className="text-ink-3">{description}</span>
      </div>
      {!isExternalManaged && (
        <button
          type="button"
          onClick={() => setCurrentPage(BILLING_PAGE)}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-fs-xs font-medium",
            "text-accent transition-colors hover:text-accent-hover",
          )}
        >
          Купить пакет <ArrowRight size={12} strokeWidth={2} />
        </button>
      )}
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink-1"
        aria-label="Скрыть уведомление"
        title="Скрыть"
      >
        <X size={14} strokeWidth={1.75} />
      </button>
    </div>
  );
};

export default NoBalanceBanner;
